// Province-aware SEO helpers. Builds head meta + canonical/og:url from the
// active ProvincePack (config.domain) so we never hard-code "phuket.thainotary.co.th".

import { getActiveProvince } from "@/provinces/_registry";
import { DEFAULT_OG_IMAGE } from "./seo";
import { polishSnippet, clampTitle } from "./ctr-snippets";

export function getActiveSiteUrl(): string {
  return getActiveProvince().config.domain.replace(/\/$/, "");
}

export function pageHeadForProvince(opts: {
  title: string;
  description: string;
  path: string; // starts with "/"
  image?: string;
  type?: "website" | "article" | "product";
  imageAlt?: string;
  /**
   * Optional hreflang alternates. Self (th-TH) and x-default pointing
   * to the Thai page are always emitted automatically. Add { lang: "en", path: "/en/..." }
   * when an English equivalent exists.
   */
  alternates?: { lang: string; path: string }[];
  locale?: "th_TH" | "en_US";
  /** Comma-joined keywords for <meta name="keywords">. Optional. */
  keywords?: string[];
}) {
  const site = getActiveSiteUrl();
  const url = `${site}${opts.path}`;
  const image = opts.image ?? DEFAULT_OG_IMAGE;
  const type = opts.type ?? "website";
  const alt = opts.imageAlt ?? opts.title;
  // English routes live under /en — infer the locale so the SERP snippet CTA
  // and og:locale match the page language even when the route omits it.
  const locale = opts.locale ?? (opts.path.startsWith("/en") ? "en_US" : "th_TH");
  // SERP snippet: strip any price, add a click-worthy CTA, cap at ~158 chars.
  const description = polishSnippet(opts.description, {
    locale,
    seed: opts.path,
  });
  // Always-on hreflang map: self + x-default. Layer user alternates on top.
  // Use bare language codes so the self-reference matches the codes used by
  // the paired page's alternates (Google requires reciprocal, identical codes).
  const alternates = [
    { lang: locale === "th_TH" ? "th" : "en", path: opts.path },
    ...(opts.alternates ?? []).map((a) => ({ lang: a.lang, path: a.path })),
    { lang: "x-default", path: opts.path },
  ];
  const alternateLinks = alternates.map((a) => ({
    rel: "alternate",
    hrefLang: a.lang,
    href: `${site}${a.path}`,
  }));
  // <title> must fit Google's ~60-char display window; og/twitter keep the
  // full authored title because social cards allow more room.
  const serpTitle = clampTitle(opts.title);
  return {
    meta: [
      { title: serpTitle },
      { name: "description", content: description },
      ...(opts.keywords && opts.keywords.length
        ? [{ name: "keywords", content: opts.keywords.join(", ") }]
        : []),
      { property: "og:title", content: opts.title },
      { property: "og:description", content: description },
      { property: "og:type", content: type },
      { property: "og:url", content: url },
      { property: "og:locale", content: locale },
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: alt },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: opts.title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
    ],
    links: [{ rel: "canonical", href: url }, ...alternateLinks],
  };
}

/** Inject {province} / {provinceEn} placeholders for reusable templates. */
export function fillProvinceTokens(template: string): string {
  const { config } = getActiveProvince();
  return template
    .replaceAll("{province}", config.nameTh)
    .replaceAll("{provinceEn}", config.nameEn);
}